import { Container, Graphics } from 'pixi.js';
import { TILE_HALF, tileToScreen } from '../sim/projection';
import { BloodSplatField, type ActiveSplat } from './bloodSplat';
import type { BloodTextures } from './bloodArt';
import type { SplatRoll } from '../sim/blood';
import { UoHueSprite } from './uoHueBatch';

/**
 * Re-exported from the wire-safe {@link ../sim/blood} home so the render layer, the
 * protocol ({@link ../net/protocol.BloodFxMessage}), and the server share one
 * definition - callers of {@link BloodLayer.splat} keep importing it from here.
 */
export type { SplatRoll } from '../sim/blood';

/**
 * The **blood-splatter** decal overlay (Blood splatter, PRD #372, Slice A): the
 * presentation half of the pure {@link BloodSplatField} state machine, drawing each
 * live splat as a tile-pinned ground decal that pops in, holds, then fades. The
 * {@link ./telegraphOverlay.TelegraphOverlay} / {@link ./arenaFx.ArenaFxOverlay}
 * shape - a child of the renderer's camera-transformed `world` container so each
 * decal pins to its ground tile and pans/zooms with the view, aged each frame from
 * the render clock, `eventMode: 'none'` so it never swallows a pointer event.
 *
 * The one deliberate difference from the FX overlays is **z**: blood is a *floor
 * decal a character walks over*, not an effect drawn on top of them, so it sorts
 * just above the terrain and **below** the depth-ranked sprites (which take zIndex
 * >= 0) - {@link BLOOD_Z}. Splats therefore never occlude a fighter standing in
 * their own gore.
 *
 * Public API is a plain client-side {@link splat} primitive taking an
 * already-rolled scatter list `[{dx, dy, art}]`: Slice A's dev hook rolls the list
 * locally, and Slice B's `BloodFxMessage` (server-rolled scatter) and Slice C's
 * arena cascade are just future drivers of the same primitive. A per-viewer
 * **"Show blood"** setting gates it via {@link setEnabled} (one early return, no
 * wire change).
 *
 * Textures come from the baked blood atlas ({@link BloodTextures}); a splat whose
 * art id has no baked texture (an unbaked host, or a `npm run convert` warn-skip)
 * degrades to a mandatory **Graphics-primitive fallback** - a small irregular
 * dark-red blob of a few overlapping circles, varied by the art index so it still
 * looks different per splat.
 */

/**
 * zIndex within the sortable `world`: just above the terrain plate (`TERRAIN_Z = -1`
 * in {@link ./gameRenderer}) and below the depth-ranked statics/mobiles (zIndex
 * >= 0), so blood reads as a floor decal underfoot rather than an on-top FX.
 */
const BLOOD_Z = -0.5;

/** The dark-red the Graphics fallback and the tinted decals read as (arterial, not bright). */
const BLOOD_COLOR = 0x8a0f0f;
/** A slightly darker rim colour, layered under the fill so the blob has some depth. */
const BLOOD_COLOR_DARK = 0x5c0808;

/** A discrete tile coordinate. */
interface Tile {
  x: number;
  y: number;
}

/** One pooled decal sprite paired with its splat id. */
interface Decal {
  gfx: Container;
}

/**
 * Draw the Graphics-primitive fallback blob into `g` for blood `art` index: a few
 * overlapping dark-red circles whose count, radii, and offsets are a deterministic
 * function of the index, so each of the 7 arts looks distinct without any texture.
 * Centred on the origin (the decal container is positioned on the tile centre).
 */
function drawFallbackBlob(g: Graphics, art: number): void {
  // A small pseudo-random spread seeded by the art index (pure, no RNG state).
  const seed = (art * 2654435761) >>> 0;
  const rand = (n: number): number => {
    const v = ((seed ^ (n * 0x9e3779b1)) >>> 0) / 0xffffffff;
    return v;
  };
  const blobs = 3 + (art % 3); // 3..5 overlapping circles
  for (let i = 0; i < blobs; i++) {
    const r = 4 + rand(i * 2) * 5; // 4..9 px radius
    const ox = (rand(i * 2 + 1) - 0.5) * 14;
    const oy = (rand(i * 2 + 3) - 0.5) * 8; // squashed vertically (dimetric ground)
    g.circle(ox, oy, r).fill({ color: i === 0 ? BLOOD_COLOR : BLOOD_COLOR_DARK, alpha: 0.85 });
  }
  // A couple of small stray droplets for texture.
  for (let i = 0; i < 2; i++) {
    const ox = (rand(i + 7) - 0.5) * 22;
    const oy = (rand(i + 11) - 0.5) * 12;
    g.circle(ox, oy, 1.5 + rand(i + 13) * 1.5).fill({ color: BLOOD_COLOR, alpha: 0.8 });
  }
}

export class BloodLayer extends Container {
  /**
   * The land elevation under a tile (terraced land, PRD #536 Slice C): wired by
   * the renderer on heighted maps so a decal splatted on a terrace draws ON the
   * raised ground, not `z * Z_STEP` px below it (a splat's fractional scatter
   * position reads the tile it rounds onto). Defaults to flat ground - on a
   * flat map every decal position is byte-identical to the pre-height layer.
   */
  landZ: (x: number, y: number) => number = () => 0;

  private readonly field = new BloodSplatField();
  /** Pooled decal sprites keyed by the field's splat id. */
  private readonly decals = new Map<number, Decal>();
  private textures: BloodTextures = new Map();
  /** The "Show blood" per-viewer gate; splats are dropped (never spawned) while off. */
  private enabled = true;
  /**
   * Whether the last materialised active set held a splat still popping in or
   * fading out. Maintained by {@link update} and by {@link splat} (a newborn
   * splat is mid-pop by definition), and read by
   * {@link requiresActivePresentation}.
   */
  private changingPresentation = false;

  constructor(textures?: BloodTextures) {
    super();
    if (textures) this.textures = textures;
    // Pure floor decal: never intercept pointer events, sort just above terrain and
    // below every sprite (the camera transform still applies, so a decal tracks its
    // tile as the view pans/zooms).
    this.eventMode = 'none';
    this.zIndex = BLOOD_Z;
    this.sortableChildren = false;
  }

  /** Late-bind the baked blood textures (the renderer loads them asynchronously). */
  setTextures(textures: BloodTextures): void {
    this.textures = textures;
  }

  /**
   * Toggle the per-viewer "Show blood" setting. When off, {@link splat} is a no-op
   * (nothing new is spawned); splats already live simply age out. No wire change -
   * this is a purely local render preference.
   */
  setEnabled(on: boolean): void {
    this.enabled = on;
  }

  /** How many splats are currently live (the renderer/tests read this). */
  get activeCount(): number {
    return this.field.size;
  }

  /**
   * True only during the pop or fade portions that visibly change each frame.
   *
   * Read from the flag {@link update} sets while it is already walking the active
   * set, rather than materialising that set a second time (PRD #826): the
   * renderer calls this once per frame right after `update`, so re-deriving every
   * live splat's alpha and scale to answer one boolean doubled the layer's
   * per-frame allocation for nothing.
   */
  get requiresActivePresentation(): boolean {
    return this.changingPresentation;
  }

  /**
   * Spray blood around victim `tile` from an already-rolled scatter list. Each
   * `{dx, dy, art}` lands on `(tile.x + dx, tile.y + dy)` with the given art index;
   * an optional `hue` is the `BloodHue` seam (wired but currently unused - splats
   * draw their natural red). A no-op while the "Show blood" setting is off.
   */
  splat(tile: Tile, splats: readonly SplatRoll[], hue?: number): void {
    if (!this.enabled) return;
    for (const s of splats) {
      const { id, evicted } = this.field.spawn(tile.x + s.dx, tile.y + s.dy, s.art, hue);
      for (const e of evicted) this.destroyDecal(e);
      this.spawnDecal(id, s.art, hue);
      // A splat born this instant is mid-pop, so the next frame must be a live
      // one even though `update` has not walked the field yet.
      this.changingPresentation = true;
    }
  }

  /** Build the pooled decal container for splat `id` (baked texture, else the blob fallback). */
  private spawnDecal(id: number, art: number, hue?: number): void {
    const container = new Container();
    const def = this.textures.size ? this.textures.get(`blood-${art}`) : undefined;
    if (def) {
      const sprite = new UoHueSprite();
      sprite.texture = def;
      sprite.anchor.set(0.5);
      // The hue seam (nothing sets it yet): a UO hue index would drive the shared hue
      // shader in a later slice; for now a supplied hue at least tints toward it so the
      // field round-trips visibly, and its absence leaves the art's natural red.
      if (hue !== undefined) sprite.tint = BLOOD_COLOR;
      container.addChild(sprite);
    } else {
      const g = new Graphics();
      drawFallbackBlob(g, art);
      container.addChild(g);
    }
    this.addChild(container);
    this.decals.set(id, { gfx: container });
  }

  private destroyDecal(id: number): void {
    const decal = this.decals.get(id);
    if (!decal) return;
    decal.gfx.destroy({ children: true });
    this.decals.delete(id);
  }

  /**
   * Advance every splat by `dtMs` (the render frame delta): reposition, scale, and
   * fade each decal from the pure field, and destroy any that aged out. A static
   * refresh (`dtMs <= 0`, e.g. a headless test frame) still reconciles placement.
   */
  update(dtMs: number): void {
    if (dtMs > 0) {
      for (const removed of this.field.advance(dtMs)) this.destroyDecal(removed);
    }
    let changing = false;
    for (const s of this.field.active()) {
      this.applyDecal(s);
      if (s.alpha < 1 || s.scale < 1) changing = true;
    }
    this.changingPresentation = changing;
  }

  /** Position/scale/fade one decal to its pure-field state. */
  private applyDecal(s: ActiveSplat): void {
    const decal = this.decals.get(s.id);
    if (!decal) return;
    const z = this.landZ(Math.round(s.tileX), Math.round(s.tileY));
    const top = tileToScreen(s.tileX, s.tileY, z);
    decal.gfx.position.set(top.x, top.y + TILE_HALF); // tile centre, on the ground at its landZ
    decal.gfx.alpha = s.alpha;
    decal.gfx.scale.set(s.scale);
  }
}
